import React, { useState, useEffect, useContext } from 'react';
import { useResolvedPath, useMatch, NavLink, useLocation, matchPath } from 'react-router-dom';
import { AppSettings } from './../../config/app-settings.js';
import menus from './../../config/app-menu.jsx';
import { slideUp } from './../../composables/slideUp.js';
import { slideToggle } from './../../composables/slideToggle.js';

function NavItem({ menu, ...props }) {
  let resolved = useResolvedPath(menu.path);
  let match = useMatch({ path: resolved.pathname });

  let location = useLocation();
  let match2 = matchPath(
    { path: menu.path, end: menu.children ? false : true },
    location.pathname
  );

  let icon = menu.icon && <div className="menu-icon"><i className={menu.icon}></i></div>;
  let img = menu.img && <div className="menu-icon-img"><img src={menu.img} alt="" /></div>;
  let caret = (menu.children && !menu.badge) && <div className="menu-caret"></div>;
  let label = menu.label && <span className="menu-label ms-5px">{menu.label}</span>;
  let badge = menu.badge && <div className="menu-badge">{menu.badge}</div>;
  let highlight = menu.highlight && <i className="fa fa-paper-plane text-theme"></i>;
  let title = menu.title && <div className="menu-text">{menu.title} {label} {highlight}</div>;

  // アコーディオンメニュー（開閉）のアニメーション制御
  useEffect(() => {
    const handleClick = function (e) {
      e.preventDefault();
      const target = this.nextElementSibling;

      // 同じ階層にある他のメニュー（兄弟要素）だけを閉じる処理
      const parentMenu = this.closest('.menu-submenu') || this.closest('.menu');
      const siblingMenus = parentMenu ? Array.from(parentMenu.children) : [];

      siblingMenus.forEach((menu) => {
        const otherTarget = menu.querySelector('.menu-submenu');
        if (otherTarget && otherTarget !== target) {
          slideUp(otherTarget, expandTime);

          const otherTargetMenuItem = otherTarget.closest('.menu-item');
          if (otherTargetMenuItem && otherTargetMenuItem.classList) {
            otherTargetMenuItem.classList.remove('expand');
            otherTargetMenuItem.classList.add('closed');
          }
        }
      });

      // クリックされたメニュー自身を開く／閉じる処理
      const targetItemElm = target?.closest('.menu-item');
      if (targetItemElm && targetItemElm.classList) {
        if (
          targetItemElm.classList.contains('expand') ||
          (targetItemElm.classList.contains('active') && !target.style.display)
        ) {
          targetItemElm.classList.remove('expand');
          targetItemElm.classList.add('closed');
          slideToggle(target, expandTime);
        } else {
          targetItemElm.classList.add('expand');
          targetItemElm.classList.remove('closed');
          slideToggle(target, expandTime);
        }
      }
    };

    const handleSidebarMenuToggle = (menus, expandTime) => {
      menus.forEach((menu) => {
        // イベントが重複して発火しないよう、古いリスナーを確実に削除してから追加する
        menu.removeEventListener('click', handleClick);
        menu.addEventListener('click', handleClick);
      });
    };

    // サイドバーのアニメーション時間（展開スピード）を取得する
    const targetSidebar = document.querySelector('.app-sidebar:not(.app-sidebar-end)');
    const expandTime = targetSidebar?.getAttribute('data-disable-slide-animation') ? 0 : 300;

    const menuBaseSelector = '.app-sidebar .menu > .menu-item.has-sub';
    const submenuBaseSelector = ' > .menu-submenu > .menu-item.has-sub';

    // クリック操作の対象となるメニュー項目（リンク）を取得する
    const menus = Array.from(document.querySelectorAll(menuBaseSelector + ' > .menu-link'));
    const submenusLvl1 = Array.from(document.querySelectorAll(menuBaseSelector + submenuBaseSelector + ' > .menu-link'));
    const submenusLvl2 = Array.from(
      document.querySelectorAll(menuBaseSelector + submenuBaseSelector + submenuBaseSelector + ' > .menu-link')
    );

    handleSidebarMenuToggle([...menus, ...submenusLvl1, ...submenusLvl2], expandTime);

    return () => {
      [...menus, ...submenusLvl1, ...submenusLvl2].forEach((menu) => {
        menu.removeEventListener('click', handleClick);
      });
    };
  }, []);

  return (
    <div className={'menu-item' + ((match || match2) ? ' active' : '') + (menu.children ? ' has-sub' : '')}>
      {!menu.children && (
        <NavLink className="menu-link" to={menu.path} {...props}>
          {img} {icon} {title}{caret} {badge}
        </NavLink>
      )}
      {menu.children && (
        <a href="#/" className="menu-link">
          {img} {icon} {title}{caret} {badge}
        </a>
      )}

      {menu.children && (
        <div className="menu-submenu" style={{ 'display': (match || match2) ? ' block' : '' }}>
          {menu.children.map((submenu, i) => (
            <NavItem key={i} menu={submenu} />
          ))}
        </div>
      )}
    </div>
  );
}

function SidebarNav() {
  const context = useContext(AppSettings);

  // 初期表示時、動的メニューの目印（プレースホルダー）を含めて非表示にしておく
  const [dynamicMenus, setDynamicMenus] = useState(
    menus.filter(menu =>
      menu.id !== 'dynamic-review-menu' &&
      menu.id !== 'dynamic-ranking-menu' &&
      menu.id !== 'dynamic-barcode-menu' &&
      menu.id !== 'dynamic-rakuten-event-menu'
    )
  );

  useEffect(() => {
    // ユーザー情報がない場合は通常の固定メニューだけを表示
    if (!context.userInfo) {
      setDynamicMenus(menus.filter(m =>
        m.id !== 'dynamic-review-menu' &&
        m.id !== 'dynamic-ranking-menu' &&
        m.id !== 'dynamic-barcode-menu' &&
        m.id !== 'dynamic-rakuten-event-menu'
      ));
      return;
    }

    const backendMalls = context.userInfo.malls || [];

    // バックエンドから送られてきたバーコード権限フラグをチェック
    const hasBarcodeAccess = context.userInfo.companies?.[0]?.has_barcode_access === true;

    // 各メニューが条件を満たした時だけ配列に追加する安全な設計
    const newMenus = menus.reduce((acc, menu) => {

      // 【A】レビュー管理
      if (menu.id === 'dynamic-review-menu') {
        if (backendMalls.length > 0) {
          acc.push({
            path: '/review',
            icon: 'fa-solid fa-star',
            title: 'レビュー管理',
            children: backendMalls.map(mall => ({
              path: mall.path,
              title: mall.title
            }))
          });
        }
      }
      // 【B】ランキング管理
      else if (menu.id === 'dynamic-ranking-menu') {
        if (backendMalls.length > 0) {
          acc.push({
            path: '/ranking',
            icon: 'fa fa-trophy',
            title: 'ランキング管理',
            children: [
              { path: '/ranking/total', title: '総合売上' },
              ...backendMalls.map(mall => ({
                path: mall.path.replace('/review/', '/ranking/'),
                title: mall.title
              }))
            ]
          });
        }
      }
      // 【C】バーコード管理
      else if (menu.id === 'dynamic-barcode-menu') {
        if (hasBarcodeAccess) {
          acc.push({
            path: '/barcode',
            icon: 'fa fa-barcode',
            title: 'バーコード・タグ',
            children: [
              { path: '/barcode', title: 'フリー印刷' },
              { path: '/barcode/template', title: 'テンプレート管理' }
            ]
          });
        }
      }
      // 【D】楽天イベント管理
      else if (menu.id === 'dynamic-rakuten-event-menu') {
        if (hasBarcodeAccess) {
          // 📁 1. 楽天ページ ➔ イベント グループ
          acc.push({
            path: '/rakuten-event',
            icon: 'fa-solid fa-newspaper',
            title: '楽天イベント',
            children: [
              { path: '/rakuten-event/pages', title: 'ページ一覧' },
              { path: '/rakuten-event/template', title: 'テンプレート管理' }
            ]
          });
          // 📁 2. 楽天ページ ➔ カート下 グループ（今回新設するもの）
          acc.push({
            path: '/rakuten-cart-under',
            icon: 'fa-solid fa-basket-shopping', // カート・買い物感のあるアイコン
            title: 'カート下商品管理',
            children: [
              { path: '/rakuten-event/products', title: '下カート商品一覧' },
              { path: '/rakuten-event/tags', title: '商品タグ・カラー管理' }
            ]
          });
        }
      }
      // 【E】それ以外の通常のメニュー（Homeなど）
      else {
        acc.push(menu);
      }

      return acc;
    }, []);

    setDynamicMenus(newMenus);
  }, [context.userInfo]);

  // サイドバーの検索・絞り込み機能
  function handleSidebarSearch(e) {
    let targetValue = e.target.value;
    targetValue = targetValue.toLowerCase();

    if (targetValue) {
      var elms = [].slice.call(document.querySelectorAll('.app-sidebar:not(.app-sidebar-end) .menu > .menu-item:not(.menu-profile):not(.menu-header):not(.menu-search), .app-sidebar:not(.app-sidebar-end) .menu-submenu > .menu-item'));
      if (elms) {
        elms.map(function (elm) {
          elm.classList.add('d-none');
          return true;
        });
      }
      var elms2 = [].slice.call(document.querySelectorAll('.app-sidebar:not(.app-sidebar-end) .has-text'));
      if (elms2) {
        elms2.map(function (elm) {
          elm.classList.remove('has-text');
          return true;
        });
      }
      var elms3 = [].slice.call(document.querySelectorAll('.app-sidebar:not(.app-sidebar-end) .expand'));
      if (elms3) {
        elms3.map(function (elm) {
          elm.classList.remove('expand');
          return true;
        });
      }
      var elms4 = [].slice.call(document.querySelectorAll('.app-sidebar:not(.app-sidebar-end) .menu > .menu-item:not(.menu-profile):not(.menu-header):not(.menu-search) > .menu-link, .app-sidebar .menu-submenu > .menu-item > .menu-link'));
      if (elms4) {
        elms4.map(function (elm) {
          var targetText = elm.textContent;
          targetText = targetText.toLowerCase();
          if (targetText.search(targetValue) > -1) {
            var targetElm = elm.closest('.menu-item');
            if (targetElm) {
              targetElm.classList.remove('d-none');
              targetElm.classList.add('has-text');
            }

            var targetElm2 = elm.closest('.menu-item.has-sub');
            if (targetElm2) {
              var targetElm3 = targetElm.querySelector('.menu-submenu .menu-item.d-none');
              if (targetElm3) {
                targetElm3.classList.remove('d-none');
              }
            }

            var targetElm4 = elm.closest('.menu-submenu');
            if (targetElm4) {
              targetElm4.style.display = 'block';

              var targetElm5 = targetElm.querySelector('.menu-item:not(.has-text)');
              if (targetElm5) {
                targetElm5.classList.add('d-none');
              }

              var targetElm6 = elm.closest('.has-sub:not(.has-text)');
              if (targetElm6) {
                targetElm6.classList.remove('d-none');
                targetElm6.classList.add('expand');

                var targetElm7 = targetElm.closest('.has-sub:not(.has-text)');
                if (targetElm7) {
                  targetElm7.classList.remove('d-none');
                  targetElm7.classList.add('expand');
                }
              }
            }
          }
          return true;
        });
      }
    } else {
      var elms5 = [].slice.call(document.querySelectorAll('.app-sidebar:not(.app-sidebar-end) .menu > .menu-item:not(.menu-profile):not(.menu-header):not(.menu-search).has-sub .menu-submenu'));
      if (elms5) {
        elms5.map(function (elm) {
          elm.removeAttribute('style');
          return true;
        });
      }

      var elms6 = [].slice.call(document.querySelectorAll('.app-sidebar:not(.app-sidebar-end) .menu > .menu-item:not(.menu-profile):not(.menu-header):not(.menu-search)'));
      if (elms6) {
        elms6.map(function (elm) {
          elm.classList.remove('d-none');
          return true;
        });
      }

      var elms7 = [].slice.call(document.querySelectorAll('.app-sidebar:not(.app-sidebar-end) .menu-submenu > .menu-item'));
      if (elms7) {
        elms7.map(function (elm) {
          elm.classList.remove('d-none');
          return true;
        });
      }

      var elms8 = [].slice.call(document.querySelectorAll('.app-sidebar:not(.app-sidebar-end) .expand'));
      if (elms8) {
        elms8.map(function (elm) {
          elm.classList.remove('expand');
          return true;
        });
      }
    }
  }

  return (
    <div className="menu">
      {context.appSidebarSearch && (
        <div className="menu-search mb-n3">
          <input type="text" className="form-control" placeholder="Sidebar menu filter..." onKeyUp={handleSidebarSearch} />
        </div>
      )}
      <div className="menu-header">Navigation</div>

      {dynamicMenus.map((menu, i) => (
        <NavItem key={i} menu={menu} />
      ))}
    </div>
  );
}

export default SidebarNav;